"use client";

import { useCallback, useEffect, useState, useSyncExternalStore } from "react";
import type { DesignerPanel } from "@/lib/designer/contracts";
import { subscribeToStorage } from "./subscribeToStorage";

const serverPanel = (): DesignerPanel => "chat";

/** Presentation memory is scoped to a dress and never grants action authority. */
export function useDesignerPanel(
  storageKey: string | null,
  identityKey: string
) {
  const [choice, setChoice] = useState<{
    identityKey: string;
    panel: DesignerPanel;
  } | null>(null);
  const readPanel = useCallback((): DesignerPanel => {
    if (!storageKey) return "chat";
    try {
      return localStorage.getItem(`designer-panel:${storageKey}`) === "design"
        ? "design"
        : "chat";
    } catch {
      return "chat";
    }
  }, [storageKey]);
  const savedPanel = useSyncExternalStore(
    subscribeToStorage,
    readPanel,
    serverPanel
  );
  const panel = choice?.identityKey === identityKey ? choice.panel : savedPanel;
  // A newly created server session acquires its durable key after the first turn.
  useEffect(() => {
    if (!storageKey || choice?.identityKey !== identityKey) return;
    try {
      localStorage.setItem(`designer-panel:${storageKey}`, choice.panel);
    } catch {
      /* Storage is optional. */
    }
  }, [choice, identityKey, storageKey]);
  const selectPanel = useCallback(
    (next: DesignerPanel) => {
      setChoice({ identityKey, panel: next });
      if (!storageKey) return;
      try {
        localStorage.setItem(`designer-panel:${storageKey}`, next);
      } catch {
        /* Storage is optional. */
      }
    },
    [storageKey, identityKey]
  );
  return { panel, selectPanel };
}
